// Collector: raw mysql / oracle / sqlite source → live syntax-error markers,
// grammar-backed by the per-engine tree-sitter error-recovery view. The
// renderer-side analogue of the MSSQL `collectSyntaxMarkers` pass for the three
// engines that publish NO LSP diagnostics (pgsql stays OUT — pgls owns it).
//
// ── CK-C ADOPTED POLICY (BINDING; full verdict in ck-c-adjudication.md) ──────
//   • REJECT the `classifyStatement().kind === 'unknown'` AND-gate and blanket
//     trailing-edge suppression — they lose `SELECT * FORM t` / `UPDATE t STE …`.
//   • A tree-sitter `ERROR` alone is NOT truth. For each recovery entry E a
//     marker is emitted iff ALL hold:
//        E.confidence === 1.0                        (never MISSING = 0.5) [*]
//        E overlaps exactly one scanner statement S
//        E.startOffset < source.trimEnd().length     (not a pure EOF prefix)
//        a syntaxWitness(engine, kind(S), S, E) exists
//     where the witness is an independent lexical corroboration: a structural
//     contradiction, or a Damerau-Levenshtein-distance-1 typo of the only
//     structural keyword allowed in a tightly-defined slot (FORM→FROM, STE→SET,
//     VAULES→VALUES, SELCT→SELECT, …). Grammar-too-strict rejections elsewhere
//     in legal SQL satisfy no witness and stay suppressed.
//   [*] RECORDED DEVIATION — MISSING may corroborate the STRUCTURAL witness
//     only: the mysql/sqlite WASMs recover the required CK-C §D fixture
//     `SELECT (1 + 2 FROM t;` with a zero-width MISSING `)` and NO ERROR node,
//     so the strict 1.0-only gate empirically contradicts §D on 2 of 3
//     engines. CK-C's stated reason to ban MISSING is valid *unfinished
//     prefixes* — a class the structural witness already excludes (unclosed
//     `(` requires an explicit terminator; dangling operators require a
//     following token or a terminator). The TYPO witness still requires a
//     confidence-1.0 ERROR, exactly per policy. Zero-FP corpus gate unchanged.
//   • ≤ 1 marker per statement; range = the witness token (squiggle `FORM`, not
//     the `t` tree-sitter marks); sorted by position. Meltdown guard: > 3
//     surviving statements → return [] (fail closed).
//   • Quoted strings / identifiers / comments are neutralized DIALECT-AWARE
//     (Oracle `q'[...]'` handled) — see `treeSitterSyntaxLex.neutralize`.
//
// Pure given `source` — no Monaco, no I/O beyond the (cached, shared) parser
// load. SqlMonaco converts offsets to positions via `model.getPositionAt`.
// The tree-sitter parse is CENTRALIZED through `getSharedErrorRecoveryView`, so
// this pass and the VQE compact hook (`useLocalSyntaxErrorEntries`) share ONE
// parse of an idle buffer — never a second `parser.parse` of the same model.

import { scanDocument, type ScanStatement } from '@/features/sql-grammar/document';
import { classifyStatement } from '@/features/sql-grammar/document/classifyStatement';
import type { SqlEngine, SqlRange } from '@/features/sql-grammar/model';

import { getSharedErrorRecoveryView } from '../sharedRecoveryView';
import type { SyntaxMarker, SyntaxMarkerCode } from './syntaxMarker';
import { findSyntaxWitness } from './treeSitterSyntaxWitness';

export type TreeSitterSyntaxEngineId = 'mysql' | 'oracle' | 'sqlite';

export type CollectTreeSitterSyntaxMarkersInput = {
  readonly source: string;
  readonly engineId: TreeSitterSyntaxEngineId;
};

const CODE_BY_ENGINE: Record<TreeSitterSyntaxEngineId, SyntaxMarkerCode> = {
  mysql: 'mysql-syntax',
  oracle: 'oracle-syntax',
  sqlite: 'sqlite-syntax',
};

/** Fail-closed meltdown guard: more than this many surviving statements ⇒ []. */
const MAX_SURVIVING_STATEMENTS = 3;

/** Statement-count cap (orchestrator resolution 3): the normalizer parses once
 *  PER scanner statement, so MAX_SOURCE_LEN alone doesn't bound parse count.
 *  Checked BEFORE the parse — a capped buffer never reaches tree-sitter. */
const MAX_STATEMENTS = 500;

export async function collectTreeSitterSyntaxMarkers(
  input: CollectTreeSitterSyntaxMarkersInput,
): Promise<readonly SyntaxMarker[]> {
  const { source, engineId } = input;
  if (!source.trim()) return [];

  const engine: SqlEngine = engineId;
  const statements = safeScanStatements(source, engine);
  if (statements.length === 0 || statements.length > MAX_STATEMENTS) return [];

  const view = await getSharedErrorRecoveryView(engineId, source);
  if (!view || view.length === 0) return [];
  const errorRanges = view.filter((e) => e.confidence === 1).map((e) => e.range);
  const missingRanges = view.filter((e) => e.confidence === 0.5).map((e) => e.range);

  const trailingEnd = source.trimEnd().length;
  const errorOverlapCount = countStatementOverlaps(errorRanges, statements);
  const missingOverlapCount = countStatementOverlaps(missingRanges, statements);

  const code = CODE_BY_ENGINE[engineId];
  const markers: SyntaxMarker[] = [];
  for (const s of statements) {
    // Qualifying recovery ranges: overlap THIS statement, overlap EXACTLY one
    // statement, and start before the trailing edge (not a pure EOF prefix).
    const qualifies = (r: SqlRange, overlapCount: number) =>
      overlapCount === 1 && rangesOverlap(r, s) && r.startOffset < trailingEnd;
    const qualifyingErrors = errorRanges.filter((r, i) => qualifies(r, errorOverlapCount[i]));
    const qualifyingMissing = missingRanges.filter((r, i) => qualifies(r, missingOverlapCount[i]));
    if (qualifyingErrors.length === 0 && qualifyingMissing.length === 0) continue;

    const kind = classifyStatement(s.text, engine).kind;
    const witness = findSyntaxWitness({
      engine,
      kind,
      statementText: s.text,
      statementStart: s.startOffset,
      explicitlyTerminated: isExplicitlyTerminated(source, s.endOffset),
      // Typo witness: confidence-1.0 ERROR ranges only (CK-C policy). MISSING
      // qualifies the statement for the STRUCTURAL witness alone (see [*]).
      errorRanges: qualifyingErrors,
    });
    if (!witness) continue;
    markers.push({
      startOffset: witness.start,
      endOffset: witness.end,
      message: witness.message,
      code,
    });
  }

  if (markers.length > MAX_SURVIVING_STATEMENTS) return [];
  return markers.sort((a, b) => a.startOffset - b.startOffset);
}

/** Per error-range count of how many scanner statements it overlaps (inclusive,
 *  mirroring the suppressor's `endOffset < start || startOffset > end` rule). */
function countStatementOverlaps(
  errorRanges: readonly SqlRange[],
  statements: readonly ScanStatement[],
): number[] {
  return errorRanges.map((r) => statements.reduce((n, s) => (rangesOverlap(r, s) ? n + 1 : n), 0));
}

function rangesOverlap(r: SqlRange, s: ScanStatement): boolean {
  if (r.endOffset < s.startOffset) return false;
  if (r.startOffset > s.endOffset) return false;
  return true;
}

/** The scanner strips the terminator from `text`/`endOffset`; a statement is
 *  explicitly terminated when the next non-whitespace source char is `;`. */
function isExplicitlyTerminated(source: string, endOffset: number): boolean {
  let i = endOffset;
  while (i < source.length && /\s/.test(source[i])) i += 1;
  return source[i] === ';';
}

function safeScanStatements(source: string, engine: SqlEngine): readonly ScanStatement[] {
  try {
    return scanDocument(source, { engine }).statements;
  } catch {
    return [];
  }
}
